import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { JsonSchemaInput } from '@ragu-components/json-schema-input';
import { componentLogger } from '@ragu-components/logger';
import { LabelsInput, OrdFormList, OrdIconStandard, OrdSecretInput } from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { UpdateVoicemailFields, dataSchema } from '@ragu-components/ord-types';
import {
  HttpError,
  ResourceContextProvider,
  useNotify,
  useRegisterMutationMiddleware,
  useTranslate,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';

import { useIngordDataProvider } from 'src/components/flowchart/hooks/useIngordDataProvider';

import type { UpdateVoicemail, Voicemail, UserRead } from '@ragu-components/ord-types';
import type { Middleware } from 'ra-core';
import type { IngordDataProvider } from 'src/lib/types';

interface VoicemailFormValues {
  labels: string[];
  preferences: {
    enabled: boolean;
    pin: string;
    quota?: number;
  };
}

const hasVoicemailChanges = (
  submittedValues: VoicemailFormValues | null,
  initialValues: VoicemailFormValues | null
): boolean => {
  if (submittedValues == null) return false;
  if (initialValues == null) return true;

  return (
    submittedValues.labels.length !== initialValues.labels.length ||
    submittedValues.labels.some((label, index) => label !== initialValues.labels[index]) ||
    submittedValues.preferences.enabled !== initialValues.preferences.enabled ||
    submittedValues.preferences.pin !== initialValues.preferences.pin ||
    (submittedValues.preferences.quota ?? 300) !== (initialValues.preferences.quota ?? 300)
  );
};

const VOICEMAIL_PIN_PATTERN = new RegExp(dataSchema.VoicemailPreferences.properties.pin.pattern);

const getResponseValues = (voicemail: Voicemail | null): VoicemailFormValues => {
  if (voicemail !== null) {
    return {
      labels: (voicemail.labels ?? [])
        .map((label) => label.id)
        .filter((labelId): labelId is string => Boolean(labelId)),
      preferences: {
        enabled: voicemail.preferences?.enabled ?? true,
        pin: voicemail.preferences?.pin ?? '',
        quota: voicemail.preferences?.quota ?? 300,
      },
    };
  }

  return {
    labels: [],
    preferences: {
      enabled: true,
      pin: '',
      quota: 300,
    },
  };
};

const normalizeVoicemailInput = (value: unknown): VoicemailFormValues => {
  if (value == null || typeof value !== 'object') {
    return getResponseValues(null);
  }
  const record = value as {
    labels?: Array<string | { id?: string }>;
    preferences?: { enabled?: boolean; pin?: string; quota?: number };
  };
  const labels = (record.labels ?? [])
    .map((label) => (typeof label === 'string' ? label : label?.id))
    .filter((labelId): labelId is string => Boolean(labelId));
  return {
    labels,
    preferences: {
      enabled: Boolean(record.preferences?.enabled),
      pin: record.preferences?.pin ?? '',
      quota: record.preferences?.quota ?? 300,
    },
  };
};

export const UserVoicemailTab = ({ user }: { user: UserRead }): React.ReactElement => {
  const dataProvider = useIngordDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const {
    clearErrors,
    getValues,
    setError,
    setValue,
    formState: { isSubmitting },
  } = useFormContext();
  const initialValuesRef = useRef<VoicemailFormValues | null>(null);
  const hasFetchedVoicemailRef = useRef(false);

  const [voicemail, setVoicemail] = useState<Voicemail | null | undefined>(undefined);
  const [voicemailMissing, setVoicemailMissing] = useState(false);
  const [allowCreate, setAllowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitTransition, setSubmitTransition] = useState(false);
  const validatePinValue = useCallback(
    (value: string | undefined): boolean => {
      if (value == null || value === '') {
        setError('voicemail.preferences.pin', { type: 'required', message: translate('ra.validation.required') });
        return false;
      }
      if (!VOICEMAIL_PIN_PATTERN.test(value)) {
        setError('voicemail.preferences.pin', {
          type: 'pattern',
          message: translate('resources.voicemails.validation.pin'),
        });
        return false;
      }
      clearErrors('voicemail.preferences.pin');
      return true;
    },
    [clearErrors, setError, translate]
  );

  const activeSchema = useMemo(
    () => (voicemail === null ? dataSchema.CreateVoicemail : dataSchema.UpdateVoicemail),
    [voicemail]
  );

  useEffect(() => {
    if (voicemail === undefined) return;

    const responseValues = getResponseValues(voicemail);
    setValue('voicemail.labels', responseValues.labels, { shouldDirty: false });
    setValue('voicemail.preferences.enabled', responseValues.preferences.enabled, { shouldDirty: false });
    setValue('voicemail.preferences.pin', responseValues.preferences.pin, { shouldDirty: false });
    setValue('voicemail.preferences.quota', responseValues.preferences.quota ?? 300, { shouldDirty: false });
    initialValuesRef.current = responseValues;
  }, [setValue, voicemail]);

  useEffect(() => {
    hasFetchedVoicemailRef.current = false;
    setLoading(true);
    dataProvider
      .getUserVoicemail(user.id)
      .then((data) => {
        setVoicemail(data);
        setVoicemailMissing(false);
        setAllowCreate(false);
        setLoadError(false);
      })
      .catch((error: unknown) => {
        if (error instanceof HttpError && error.status === 404) {
          setVoicemail(null);
          setVoicemailMissing(true);
          setLoadError(false);
          return;
        }
        componentLogger.error('Failed to fetch user voicemail', { error });
        notify('resources.voicemails.notifications.fetchError', { type: 'error' });
        setLoadError(true);
      })
      .finally(() => {
        hasFetchedVoicemailRef.current = true;
        setLoading(false);
      });
  }, [dataProvider, notify, user.id]);

  const handleSave = useCallback(
    async (values: VoicemailFormValues): Promise<void> => {
      if (voicemailMissing && !allowCreate) {
        return;
      }

      const payloadBase = {
        labels: values.labels ?? [],
        preferences: {
          enabled: values.preferences?.enabled ?? true,
          pin: values.preferences?.pin ?? '',
          quota: values.preferences?.quota ?? 300,
        },
      };

      setSaving(true);
      try {
        let result: Voicemail;
        if (voicemail === null || voicemailMissing) {
          const payload = payloadBase;
          result = await dataProvider.createUserVoicemail(user.id, payload);
          notify('resources.voicemails.notifications.createSuccess', { type: 'info' });
        } else {
          const payload: UpdateVoicemail = payloadBase;
          result = await dataProvider.updateUserVoicemail(user.id, payload);
          notify('resources.voicemails.notifications.updateSuccess', { type: 'info' });
        }
        setVoicemail(result);
        setVoicemailMissing(false);
        setAllowCreate(false);
      } catch (error) {
        componentLogger.error('Failed to save user voicemail', { error });
        notify('resources.voicemails.notifications.updateError', { type: 'error' });
      } finally {
        setSaving(false);
      }
    },
    [allowCreate, dataProvider, notify, user.id, voicemail, voicemailMissing]
  );

  const middleware = useCallback<Middleware<IngordDataProvider['update']>>(
    async (resource, params, next) => {
      const submittedVoicemail =
        resource === Resources.users && params?.data != null && typeof params.data === 'object'
          ? (params.data as Record<string, unknown>).voicemail
          : undefined;
      const submittedValues = submittedVoicemail === undefined ? null : normalizeVoicemailInput(submittedVoicemail);
      const isCurrentUserUpdate = resource === Resources.users && params.id === user.id;
      const hasFetchedVoicemail = hasFetchedVoicemailRef.current;
      const initialValues = initialValuesRef.current;
      const voicemailReady = hasFetchedVoicemail && voicemail !== undefined;
      const voicemailChanged = allowCreate
        ? voicemailReady && submittedValues != null
        : voicemailReady && initialValues !== null && hasVoicemailChanges(submittedValues, initialValues);
      const shouldValidateVoicemailPin = isCurrentUserUpdate && voicemailChanged && (!voicemailMissing || allowCreate);

      if (shouldValidateVoicemailPin && submittedValues != null && !validatePinValue(submittedValues.preferences.pin)) {
        return Promise.reject(new Error('Invalid voicemail pin'));
      }

      if (isCurrentUserUpdate) {
        setSubmitTransition(true);
      }

      if (resource === Resources.users && params?.data != null) {
        // Remove nested voicemail from main payload to avoid validation errors.

        const { voicemail: _voicemail, ...rest } = params.data as Record<string, unknown>;
        params = { ...params, data: rest };
      }

      const result = await next(resource, params);

      try {
        if (resource !== Resources.users || params.id !== user.id) {
          return result;
        }
        if (voicemail === undefined) {
          return result;
        }
        if (!voicemailChanged) {
          return result;
        }

        if (submittedValues != null) {
          await handleSave(submittedValues);
        }
        return result;
      } finally {
        if (isCurrentUserUpdate) {
          setSubmitTransition(false);
        }
      }
    },
    [allowCreate, handleSave, user.id, validatePinValue, voicemail, voicemailMissing]
  );

  useRegisterMutationMiddleware(middleware);

  const showLoader = loading || saving || isSubmitting || submitTransition;
  const isDisabled = showLoader || loadError || (voicemailMissing && !allowCreate);
  const showEnableButton = voicemailMissing && !allowCreate && !loadError && !showLoader;
  const showFormFields = !showLoader && !loadError && (!voicemailMissing || allowCreate);

  return (
    <ResourceContextProvider value={Resources.voicemails}>
      <Stack spacing={2} sx={{ width: '100%' }}>
        {showLoader && (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} aria-label={translate('resources.voicemails.notifications.loading')} />
          </Box>
        )}
        {loadError && (
          <Typography variant='body2' color='error'>
            {translate('resources.voicemails.notifications.fetchError')}
          </Typography>
        )}
        {showEnableButton && (
          <Box sx={{ width: '100%' }}>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              {translate('resources.voicemails.helperTexts.notConfigured')}
            </Typography>
            <Button
              variant='contained'
              onClick={() => {
                setAllowCreate(true);
              }}
            >
              {translate('resources.voicemails.actions.enable')}
            </Button>
          </Box>
        )}
        {showFormFields && (
          <Box sx={{ width: '100%' }}>
            <OrdFormList icon={<OrdIconStandard name='voicemail' />}>
              <JsonSchemaInput
                source='voicemail.preferences.enabled'
                property={UpdateVoicemailFields['preferences.enabled']}
                jsonSchema={activeSchema}
                schemas={dataSchema}
                label='resources.voicemails.fields.preferences.enabled'
                disabled={isDisabled}
                defaultValue={true}
              />
              <OrdSecretInput
                source='voicemail.preferences.pin'
                label='resources.voicemails.fields.preferences.pin'
                disabled={isDisabled}
                fullWidth
                onBlur={() => {
                  validatePinValue(getValues('voicemail.preferences.pin') as string | undefined);
                }}
              />
              <JsonSchemaInput
                source='voicemail.preferences.quota'
                property={UpdateVoicemailFields['preferences.quota']}
                jsonSchema={activeSchema}
                schemas={dataSchema}
                label='resources.voicemails.fields.preferences.quota'
                disabled={isDisabled}
              />
              <LabelsInput
                source='voicemail.labels'
                label='resources.voicemails.fields.labels.name'
                disabled={isDisabled}
                fullWidth
              />
            </OrdFormList>
          </Box>
        )}
      </Stack>
    </ResourceContextProvider>
  );
};
